import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { SearchInput, SearchInputProps } from 'src/modules/form/field/SearchInput/SearchInput';
import { IconImage } from 'src/modules/global';
import styles from './LocationInput.module.scss';
import { Form } from 'antd';

type SearchInputPropsWithoutChildren = Omit<SearchInputProps, 'children' | 'onChange'>;

export type Coordinates = { lat: number; lng: number };

export interface LocationInputProps extends SearchInputPropsWithoutChildren {
  useCurrentLocationOnMount: boolean;
  map: any;
}

const LOACTION_FETCH_TIMEOUT_MS = 5000;

export const LocationInput = ({
  name,
  map,
  form,
  placeholderKey,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  useCurrentLocationOnMount = false,
  ...rest
}: LocationInputProps) => {
  const textFieldName = `${name}Text`;
  const typeFieldName = `${name}Type`;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<google.maps.places.QueryAutocompletePrediction[]>(
    []
  );
  const [showLocationButton, setShowLocationButton] = useState<boolean>(true);
  const [address, setAddress] = useState<string>(form.getFieldValue(textFieldName));
  const locationError = useCallback((errorMessage) => {
    console.error(errorMessage);
    setIsLoading(false);
    setShowLocationButton(false);
  }, []);
  const getPlaceCoordinates = useCallback(async (placeId) => {
    return new Promise((resolve, reject) => {
      const service = new google.maps.Geocoder();

      service.geocode({ placeId: placeId }, (results, status) => {
        if (status === 'OK') {
          const location = results[0].geometry.location;
          const pos = { lat: location.lat(), lng: location.lng() };

          resolve(pos);
        } else {
          reject(new Error(`Can't get place coordinates. Geocoder status '${status}'`));
        }
      });
    });
  }, []);

  const getPlaceNameFromCoordinates = useCallback(async (coordinates: Coordinates): Promise<
    string
  > => {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode(
        {
          location: new google.maps.LatLng(coordinates.lat, coordinates.lng),
        },
        (predictions, status) => {
          if (status != 'OK') {
            reject(
              new Error(
                `Can't get place name from coordinates. AutocompleteService status '${status}'`
              )
            );
          } else {
            if (!predictions.length) {
              reject(
                new Error(
                  `Can't get place name from coordinates. Autocomplete was not able to find any suggestions`
                )
              );
            }

            resolve(predictions[0].formatted_address);
          }
        }
      );
    });
  }, []);

  const setCoordinatesFromPlace = async (placeName: string, placeId: string) => {
    const coordinates = await getPlaceCoordinates(placeId);

    form.setFieldsValue({
      [name]: coordinates,
      [textFieldName]: placeName,
      [typeFieldName]: 'address-text-input',
    });
    form.submit();
  };

  // Go to user's current location
  const setCurrentLocation = useCallback(async () => {
    setIsLoading(true);

    //Check if Geolocation is supported
    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          const address = await getPlaceNameFromCoordinates(pos);

          // Check if map exists to go to current location on the map
          form.setFieldsValue({
            [name]: pos,
            [textFieldName]: address,
            [typeFieldName]: 'current-user-location',
          });

          setIsLoading(false);
          form.submit();
        },
        (e) => {
          console.error(e);
          locationError('Error when retrieving current location');
        },
        {
          timeout: LOACTION_FETCH_TIMEOUT_MS,
        }
      );
    } else {
      // Browser doesn't support Geolocation
      locationError(`Browser doesn't support Geolocation`);
    }
  }, [form, getPlaceNameFromCoordinates, locationError, name, textFieldName, typeFieldName]);

  const getValueFromSuggestion = useCallback(
    (suggestion: google.maps.places.QueryAutocompletePrediction) => {
      return suggestion.description;
    },
    []
  );

  // update suggestions based on text search
  useEffect(() => {
    if (address?.length) {
      const autocomplete = new google.maps.places.AutocompleteService();

      autocomplete.getPlacePredictions(
        {
          input: address,
          //  types: ['(cities)']
        },
        (predictions, status) => {
          if (
            ![
              google.maps.places.PlacesServiceStatus.OK,
              google.maps.places.PlacesServiceStatus.ZERO_RESULTS,
            ].includes(status)
          ) {
            console.error(status);
            return;
          }

          setSuggestions(predictions || []);
        }
      );
    } else {
      form.setFieldsValue({
        [name]: null,
        [typeFieldName]: 'none',
      });
    }
  }, [address, form, name, typeFieldName]);

  const googleServicesInitialized = !!map;

  // set current location automatically
  useEffect(() => {
    if (useCurrentLocationOnMount && googleServicesInitialized) {
      setCurrentLocation();
    }
  }, [setCurrentLocation, useCurrentLocationOnMount, googleServicesInitialized]);

  return (
    <>
      <Form.Item name={name} style={{ display: 'none' }} />
      <Form.Item name={typeFieldName} style={{ display: 'none' }} />
      <SearchInput
        name={textFieldName}
        form={form}
        placeholderKey={placeholderKey}
        leftIcon={showLocationButton && IconImage.FindLocation}
        leftIconLoading={showLocationButton && isLoading}
        onLeftIconClick={
          showLocationButton &&
          (() => {
            setCurrentLocation();
          })
        }
        searchOnDelete={true}
        suggestions={suggestions}
        updateSuggestions={(searchTerm) => setAddress(searchTerm)}
        getValueFromSuggestion={getValueFromSuggestion}
        onSuggestionSelectedByEnterPress={(s) => {
          setCoordinatesFromPlace(s.description, s.place_id);
        }}
        enforceSuggestionValue={true}
        {...rest}>
        {({ suggestionsVisible, closeSuggestions, selectedSuggestionIndex }) => (
          <div
            className={classnames(
              styles.suggestions,
              !suggestionsVisible && styles.suggestionsHidden
            )}>
            {!!suggestions &&
              suggestions.map((s, i) => (
                <div
                  className={classnames(
                    styles.suggestion,
                    i === selectedSuggestionIndex && styles.selectedSuggestion
                  )}
                  key={s.place_id}
                  onClick={() => {
                    closeSuggestions();
                    setCoordinatesFromPlace(s.description, s.place_id);
                  }}>
                  <span className={styles.suggestionText}>{s.description}</span>
                  <div className={styles.fadeOutBox} />
                </div>
              ))}
          </div>
        )}
      </SearchInput>
    </>
  );
};
